import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api/http.js';
import { useAdminAuth } from '../auth/AdminAuthContext.jsx';

/**
 * Polls /api/admin/queries/counts every 30s to drive the "Support
 * Queries" sidebar badge with the count of OPEN tickets — i.e.
 * tickets awaiting a staff response. The same shape as the user-side
 * useSupportUnread hook, just inverted: instead of counting STAFF
 * messages newer than the user's last seen, we count tickets in the
 * OPEN bucket which is the admin's "unread" equivalent.
 *
 * Cadence picks:
 *   - 30s steady-state poll (matches user-side hook)
 *   - Immediate re-poll on tab focus, so an admin coming back to the
 *     dashboard after stepping away sees a fresh count without the
 *     30s delay
 *   - `refresh()` is exposed so the Queries page can re-trigger it
 *     after sending a reply (the ticket flips OPEN → PENDING and the
 *     badge should drop in real time, not lag the user's action)
 *
 * Read-only / Support-agent / Lead / Superadmin all see the badge.
 * The endpoint is allowed for all of those roles at the controller
 * level; we don't gate further on the client side.
 *
 * Returns { open, pending, closed, total, refresh }.
 */
const POLL_MS = 30_000;

export function useAdminQueriesCounts() {
  const { me } = useAdminAuth();
  const authed = !!me;
  const [counts, setCounts] = useState({ open: 0, pending: 0, closed: 0, total: 0 });

  // Track the previous observation of `open` so we can play a ping
  // sound on positive deltas — i.e. a user just posted a new message
  // that flipped a ticket to OPEN (or created a fresh one). Held in a
  // ref because the polling callback re-runs without re-mounting; if
  // we held it in state every refresh would create a stale closure.
  //
  // `bootstrappedRef` flips true after the FIRST successful refresh.
  // That way an admin opening the dashboard to a backlog of 7 open
  // tickets doesn't get a ping for each of those — only for tickets
  // that come in AFTER they sat down.
  const prevOpenRef = useRef(0);
  const bootstrappedRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!authed) return;
    try {
      const r = await api('/api/admin/queries/counts');
      const open    = Number(r?.open    || 0);
      const pending = Number(r?.pending || 0);
      const closed  = Number(r?.closed  || 0);
      const total   = Number(r?.total   || 0);

      // Ping only after we have a baseline AND open went up.
      // Going from 2 → 5 plays one ping, not three — the chime is
      // an "attention" cue, not a per-ticket signal.
      if (bootstrappedRef.current && open > prevOpenRef.current) {
        playPing();
      }
      prevOpenRef.current = open;
      bootstrappedRef.current = true;

      setCounts({ open, pending, closed, total });
    } catch {
      // Network blip / 401 — keep last known counts. The next poll
      // catches up; a sustained 401 will get routed by the global
      // api() unauth handler anyway.
    }
  }, [authed]);

  useEffect(() => {
    if (!authed) {
      setCounts({ open: 0, pending: 0, closed: 0, total: 0 });
      // Reset the baseline so the next sign-in re-bootstraps the
      // "no-ping on first observation" guard. Otherwise the second
      // admin to sign in on the same browser would hear a ping for
      // every ticket already in the OPEN bucket.
      prevOpenRef.current = 0;
      bootstrappedRef.current = false;
      return undefined;
    }
    refresh();
    const id = window.setInterval(refresh, POLL_MS);
    return () => window.clearInterval(id);
  }, [authed, refresh]);

  // Tab focus → poll immediately.
  useEffect(() => {
    if (!authed) return undefined;
    const onVis = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [authed, refresh]);

  // Window-level event bus so the Queries page (and any future caller)
  // can request an immediate re-poll without holding a ref to the
  // hook itself. Used after a reply lands so the OPEN→PENDING flip
  // shows up in the sidebar badge instantly, not after the 30s
  // interval fires.
  //
  //   window.dispatchEvent(new CustomEvent('noteshq:queries-counts-refresh'));
  useEffect(() => {
    if (!authed) return undefined;
    const onPing = () => refresh();
    window.addEventListener('noteshq:queries-counts-refresh', onPing);
    return () => window.removeEventListener('noteshq:queries-counts-refresh', onPing);
  }, [authed, refresh]);

  return { ...counts, refresh };
}

// ── Ping sound ──────────────────────────────────────────────────────
//
// Two short sine pulses generated through WebAudio — same pattern as
// noteshq-web's useSupportUnread, but pitched slightly lower (700/900
// Hz vs 880/1175 Hz) so the admin tone is distinguishable from the
// user-side one if both happen to be open in the same browser
// session (e.g. a support agent who's also a NotesHQ customer).
//
// Zero KB to the bundle vs shipping an audio asset. AudioContext is
// baseline in modern browsers. Browser autoplay policies block sound
// until the user has interacted with the page at least once; first
// ping after a cold load might be silent — every later one plays
// correctly. We don't surface this to the operator (a silent first
// ping is mildly annoying but a "click here to enable sound" overlay
// would be worse).

let audioCtx = null;
function getAudioContext() {
  if (audioCtx) return audioCtx;
  try {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return null;
    audioCtx = new Ctor();
    return audioCtx;
  } catch {
    return null;
  }
}

function playPing() {
  const ctx = getAudioContext();
  if (!ctx) return;
  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }
  try {
    pulse(ctx, ctx.currentTime + 0.00, 700, 0.10);
    pulse(ctx, ctx.currentTime + 0.13, 900, 0.10);
  } catch {
    // Autoplay-blocked first time around — ignored.
  }
}

function pulse(ctx, when, freq, durSec) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.value = freq;
  // Quick envelope to avoid click on attack/release.
  gain.gain.setValueAtTime(0.0001, when);
  gain.gain.exponentialRampToValueAtTime(0.14, when + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, when + durSec);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(when);
  osc.stop(when + durSec + 0.02);
}
